import * as _ from 'lodash';
import { Dispatch, connect } from 'react-redux';
import { useEffect, useState } from 'react';
import {
    AppActionCreators, IApplicationState, IMicrositeLookupDataResponse, IMicrositeShortCodeResponse, INewDonorRecipientlookupDataResponse,
    IPassMicrositeRecipientProfile, MicrositeActionCreators, NewDonorRecipientActionCreators
} from '@nkr/store/index';
import { IContact, IListItem, IMedicalRecord } from "@nkr/models/index"
import { Alert, Button, Col, ControlLabel, FormControl, FormGroup, InputGroup, Modal, Panel, PanelGroup, Row } from "react-bootstrap";
import { ShortCodeType } from '@nkr/enums/short-code-type';
import { PassAutomatedProfiles } from '@nkr/enums/pass-automated-emails';

interface IValidation {
    shortCode: boolean,
    mrn: boolean,
    abo: boolean,
    alias: boolean,
    tissueTyping: boolean,
    micrositeType: boolean,
    coordinator: boolean,
    language: boolean
}

interface IValidationState {
    validation: IValidation,
    isValid: boolean
}

interface IProps {
    show: boolean,
    data: IMedicalRecord,
    centerId: number,
    setupMicrosite: boolean,
    setupRecipient: boolean,
    profileAutomationErrors?: string | null,
    onSave: (profileData: IPassMicrositeRecipientProfile) => void,
    onCancel: () => void
}

interface IStateProps {
}

interface IState {
    shortCode: string,
    mrn: string,
    abo: number,
    alias: string,
    tissueTyping: number,
    micrositeType: number,
    coordinator: number,
    language: number,
}

interface IDispatchProps {
    showLoading: (message: string) => Promise<void>,
    hideLoading: () => void,
    getMicrositeLookUpData: (centerId: number) => Promise<IMicrositeLookupDataResponse>,
    getRecipientProfileLookUpData: (centerId: number) => Promise<INewDonorRecipientlookupDataResponse>,
    getShortCode: () => Promise<IMicrositeShortCodeResponse>,
}

type TProps = IProps & IStateProps & IDispatchProps;

const ProfileCreation = (props: TProps) => {
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const [coordinatorList, setCoordinatorList] = useState<IContact[]>([]);
    const [aboTypeList, setAboTypeList] = useState<IListItem[]>([]);
    const [languageList, setLanguageList] = useState<IListItem[]>([]);
    const [micrositeTypeList, setMicrositeTypeList] = useState<IListItem[]>([]);

    const [state, setState] = useState<IState>({
        shortCode: '',
        mrn: '',
        abo: -1,
        alias: '',
        tissueTyping: -1,
        micrositeType: -1,
        coordinator: -1,
        language: -1
    });

    const [validationState, setValidation] = useState<IValidationState>({
        validation: {
            shortCode: true,
            mrn: true,
            abo: true,
            alias: true,
            tissueTyping: true,
            micrositeType: true,
            coordinator: true,
            language: true
        },
        isValid: true
    })

    useEffect(() => {
        props.showLoading("Retrieving setup data...").then(() => {
            Promise.all([
                props.getMicrositeLookUpData(props.centerId),
                props.getRecipientProfileLookUpData(props.centerId),
                props.getShortCode()
            ])
                .then(([micrositeLookUp, recipientProfileLookUp, shortCode]) => {
                    let micrositeData = micrositeLookUp.data;
                    let recipientProfileData = recipientProfileLookUp.data;
                    let shortCodeData = shortCode.data;

                    if (micrositeData.success && recipientProfileData.success && shortCodeData.success) {
                        setState(prevState => ({
                            ...prevState,
                            shortCode: shortCodeData.results,
                        }));

                        setCoordinatorList(micrositeData.results.coordinatorList);
                        setAboTypeList(recipientProfileData.results.bloodTypeList);
                        setLanguageList(micrositeData.results.languageList);
                        setMicrositeTypeList(micrositeData.results.micrositeTypeList);

                        if (props.setupMicrosite && props.data.isDashLiverParticipant == false) {
                            setState(prev => ({ ...prev, micrositeType: 0 }));
                        }
                    }
                })
                .finally(() => {
                    props.hideLoading()
                });
        })
    }, [])

    const handleGetShortCode = () => {
        props.getShortCode().then(response => {
            if (response.data.success) {
                setState(prevState => ({
                    ...prevState,
                    shortCode: response.data.results
                }));
            }
        });
    }

    const validate = () => {
        let validation: IValidation = {
            shortCode: !props.setupMicrosite || state.shortCode != '',
            mrn: !props.setupRecipient || state.mrn != '',
            abo: state.abo > -1,
            alias: !props.setupRecipient || state.alias != '',
            tissueTyping: !props.setupRecipient || state.tissueTyping > -1,
            micrositeType: !props.setupMicrosite || state.micrositeType > -1,
            coordinator: !props.setupMicrosite || state.coordinator > -1,
            language: !props.setupMicrosite || state.language > -1
        }

        let isValid =
            validation.shortCode
            && validation.mrn
            && validation.abo
            && validation.alias
            && validation.tissueTyping
            && validation.micrositeType
            && validation.coordinator
            && validation.language

        return { validation, isValid };
    }

    const handleDataSave = () => {
        let { validation, isValid } = validate();

        setValidation(prevValidation => ({
            validation,
            isValid
        }))

        setIsDirty(true);

        if (isValid) {
            let type = PassAutomatedProfiles.Both;

            if (!props.setupMicrosite && props.setupRecipient) {
                type = PassAutomatedProfiles.RecipientProfile;
            }

            if (props.setupMicrosite && !props.setupRecipient) {
                type = PassAutomatedProfiles.Microsite;
            }

            let profileData: IPassMicrositeRecipientProfile = {
                type,
                shortCode: state.shortCode,
                mrn: state.mrn,
                abo: state.abo,
                alias: state.alias,
                tissueTyping: state.tissueTyping,
                micrositeType: state.micrositeType,
                coordinator: state.coordinator,
                language: state.language
            }

            props.onSave(profileData);
        }
    }

    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement & FormControl>) => {
        let { name, value } = e.target;

        setState(prev => ({ ...prev, [name]: value }));

        let { validation } = validate();

        setValidation(prevValidation => ({
            ...prevValidation,
            validation
        }))
    }

    const handleSelectNumberChange = (e: React.ChangeEvent<HTMLSelectElement & FormControl>) => {
        let { name, value } = e.target;

        setState(prev => ({ ...prev, [name]: parseInt(value, 10) }));

        let { validation } = validate();

        setValidation(prevValidation => ({
            ...prevValidation,
            validation
        }))
    }

    const filteredCoordinatorList = props.centerId > 0 ? _.sortBy(coordinatorList.filter(x => x.entityTypeId == props.centerId), item => item.fullName) : [];
    const { mrn, alias, shortCode, abo, tissueTyping, micrositeType, coordinator, language } = validationState.validation;

    return (
        <Modal backdrop='static' show={props.show} onHide={props.onCancel}>
            <Modal.Header closeButton>
                <Modal.Title>Needed Fields for Automated Profile Creation</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {props.profileAutomationErrors != null && (
                    <Alert bsStyle='danger'>
                        {props.profileAutomationErrors}
                    </Alert>
                )}

                {validationState.isValid == false &&
                    <Alert bsStyle='danger' bsSize='sm'>
                        The items in red are required to save...
                    </Alert>
                }

                <PanelGroup style={{ marginBottom: 0 }}>
                    {props.setupRecipient && (
                        <Panel>
                            <Panel.Heading>Missing Recipient Profile Information</Panel.Heading>
                            <Panel.Body>
                                <Row>
                                    <Col md={6}>
                                        <FormGroup validationState={!alias && isDirty ? 'error' : null}>
                                            <ControlLabel>Alias</ControlLabel>
                                            <FormControl
                                                type={'text'}
                                                name={'alias'}
                                                placeholder={'Alias'}
                                                value={state.alias}
                                                maxLength={12}
                                                onChange={handleTextChange}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup validationState={!mrn && isDirty ? 'error' : null}>
                                            <ControlLabel>MRN</ControlLabel>
                                            <FormControl
                                                type={'text'}
                                                name={'mrn'}
                                                placeholder={'MRN'}
                                                value={state.mrn}
                                                maxLength={24}
                                                onChange={handleTextChange}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <FormGroup validationState={!tissueTyping && isDirty ? 'error' : null}>
                                            <ControlLabel>Ship High Res Tissue Typing?</ControlLabel>
                                            <FormControl
                                                componentClass={'select'}
                                                name={'tissueTyping'}
                                                value={state.tissueTyping}
                                                onChange={handleSelectNumberChange}
                                            >
                                                <option value={-1}>- Please Select -</option>
                                                <option value={1}>Yes</option>
                                                <option value={0}>No</option>
                                            </FormControl>
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </Panel.Body>
                        </Panel>
                    )}

                    {props.setupMicrosite && (
                        <Panel>
                            <Panel.Heading>Missing Microsite Information</Panel.Heading>
                            <Panel.Body>
                                <Row>
                                    <Col md={6}>
                                        <FormGroup validationState={!shortCode && isDirty ? 'error' : null}>
                                            <ControlLabel>Short Code</ControlLabel>
                                            <InputGroup>
                                                <FormControl readOnly={true} maxLength={6} value={state.shortCode} />
                                                <InputGroup.Button>
                                                    <Button onClick={handleGetShortCode}>
                                                        <span className='glyphicon glyphicon-refresh'></span> Regenerate
                                                </Button>
                                                </InputGroup.Button>
                                            </InputGroup>
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        {props.data.isDashLiverParticipant &&
                                            <FormGroup validationState={!micrositeType && isDirty ? 'error' : null}>
                                                <ControlLabel>Microsite Type</ControlLabel>
                                                <FormControl
                                                    componentClass={'select'}
                                                    name={'micrositeType'}
                                                    value={state.micrositeType}
                                                    onChange={handleSelectNumberChange}
                                                >
                                                    <option value={-1}>- Please Select -</option>
                                                    {micrositeTypeList.map(item =>
                                                        <option key={'microsite-' + item.id} value={item.id}>{item.description}</option>
                                                    )}
                                                </FormControl>
                                            </FormGroup>
                                        }
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <FormGroup validationState={!coordinator && isDirty ? 'error' : null}>
                                            <ControlLabel>Coordinator</ControlLabel>
                                            <FormControl
                                                componentClass={'select'}
                                                name={'coordinator'}
                                                value={state.coordinator}
                                                onChange={handleSelectNumberChange}
                                            >
                                                <option value={-1}>- Please Select -</option>
                                                {filteredCoordinatorList.map(item =>
                                                    <option key={'coordinator-' + item.id} value={item.id}>{item.fullName}</option>
                                                )}
                                            </FormControl>
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup validationState={!language && isDirty ? 'error' : null}>
                                            <ControlLabel>Language Preference</ControlLabel>
                                            <FormControl
                                                componentClass={'select'}
                                                name={'language'}
                                                value={state.language}
                                                onChange={handleSelectNumberChange}
                                            >
                                                <option value={-1}>- Please Select -</option>
                                                {languageList.map(item =>
                                                    <option key={'coordinator-' + item.id} value={item.id}>{item.description}</option>
                                                )}
                                            </FormControl>
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </Panel.Body>
                        </Panel>
                    )}

                    <Panel>
                        <Panel.Heading>Missing ABO Information</Panel.Heading>
                        <Panel.Body>
                            <Row>
                                <Col md={6}>
                                    <FormGroup validationState={!abo && isDirty ? 'error' : null}>
                                        <ControlLabel>Blood Type</ControlLabel>
                                        <FormControl
                                            componentClass={'select'}
                                            name={'abo'}
                                            value={state.abo}
                                            onChange={handleSelectNumberChange}
                                        >
                                            <option value={-1}>- Please Select -</option>
                                            {aboTypeList.map(item =>
                                                <option key={'abo-' + item.id} value={item.id}>{item.description}</option>
                                            )}
                                        </FormControl>
                                    </FormGroup>
                                </Col>
                            </Row>
                        </Panel.Body>
                    </Panel>
                </PanelGroup>
            </Modal.Body>
            <Modal.Footer>
                <Button bsStyle='primary' onClick={handleDataSave}>Save</Button>
                <Button onClick={props.onCancel}>Cancel</Button>
            </Modal.Footer>
        </Modal>
    );
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        app: state.app
    };
}

const mapDispatchToProps = (dispatch: Dispatch<any>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            return dispatch(AppActionCreators.hideLoading());
        },
        getMicrositeLookUpData: (centerId) => {
            return dispatch(MicrositeActionCreators.getLookupData(centerId, undefined));
        },
        getRecipientProfileLookUpData: (centerId) => {
            return dispatch(NewDonorRecipientActionCreators.getNewDonorRecipientLookupData(centerId));
        },
        getShortCode: () => {
            return dispatch(MicrositeActionCreators.getShortCode(ShortCodeType.Microsite));
        },
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(ProfileCreation);